import React, { useState, useEffect } from 'react';
import clsx from 'clsx';

import { BaseActionButtonProps } from '../BaseActionButtonProps';

import commonStyles from '../ActionButtonCommon.module.scss';
import styles from './EmailButton.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { encodeURIComponentSafe } from 'lib/utils/string-utils';
import { useContentItem } from 'lib/hooks/useContentItem';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';

interface EmailButtonProps extends BaseActionButtonProps {
  isSubAction?: boolean;
}

export function EmailButton({
  label,
  variant,
  isSubAction = false,
  toolbarIdentifier,
  eventPositionSuffix,
}: EmailButtonProps): JSX.Element {
  const contentItem = useContentItem();
  const { getDictionaryValue } = useDictionary();

  const [pageUrl, setPageUrl] = useState(contentItem?.PageUrl || '');
  const [pageName, setPageName] = useState(contentItem?.Title || '');
  const [metaDescription, setMetaDescription] = useState(contentItem?.Description || '');

  // Update with meta tag values on client side after hydration
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const ogUrl = document.querySelector('meta[property="og:url"]')?.getAttribute('content');
      if (ogUrl) {
        setPageUrl(ogUrl);
      } else if (!contentItem?.PageUrl && typeof window !== 'undefined') {
        setPageUrl(window.location.href);
      }

      if (!contentItem?.Title && document.title) {
        setPageName(document.title);
      }

      const descMeta = document.querySelector('meta[name="description"]')?.getAttribute('content');
      if (descMeta) {
        setMetaDescription(descMeta);
      }
    }
  }, [contentItem?.PageUrl, contentItem?.Title]);

  const contentType = contentItem?.ContentType || 'page';
  const { siteName } = useGlobalData();
  const checkoutText =
    getDictionaryValue('Components.ActionToolBar.CheckoutThis') || 'Check out this';
  const readMoreText = getDictionaryValue('Components.ActionToolBar.ReadMore') || 'Read more';
  const subject = checkoutText
    .replace('{ContentType}', contentType.toLowerCase())
    .replace('{PageName}', pageName)
    .replace('{SiteName}', siteName);
  const body = `${readMoreText.replace('{PageUrl}', pageUrl).replace('{PageMetaDescription}', metaDescription)}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponentSafe(subject)}&body=${encodeURIComponentSafe(body)}`;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (typeof window !== 'undefined') {
      window.location.href = mailtoUrl;
    }
  };

  const getGtmPosition = (): string => {
    return [toolbarIdentifier, eventPositionSuffix].filter(Boolean).join('.') || '';
  };

  const gtmEvent = {
    event: 'email',
    position: getGtmPosition(),
  };

  return (
    <div
      className={clsx({
        [commonStyles['actionButtonWrapper--horizontal']]: variant === 'horizontal',
      })}
    >
      <CtaLink
        ctaStyle={variant === 'horizontal' && !isSubAction ? 'link-invert-underline' : 'none'}
        ctaVariant="brand"
        url={mailtoUrl}
        suppressNewTabIcon
        className={clsx(
          commonStyles.actionButtonBase,
          commonStyles[`actionButton--${variant}`],
          isSubAction && commonStyles['actionButtonSubAction']
        )}
        onClick={handleClick}
        srOnlyText={`${getDictionaryValue('Components.ActionToolBar.EmailScreenReaderText') || ', opens default mail client'}`}
        gtmEvent={gtmEvent}
        ctaIconBefore="mail"
        ctaIconSize="sm"
        ctaIconNoSpacer={variant === 'vertical'}
        ctaIconClassName={styles.icon}
      >
        {label}
      </CtaLink>
    </div>
  );
}
